"use client";
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { CiMenuFries } from "react-icons/ci";
import { IoClose } from "react-icons/io5";
import { motion, AnimatePresence } from "framer-motion";
import { HiMiniChevronRight } from "react-icons/hi2";
import { IconSettings, IconLogout2, IconReceipt2, IconUsersGroup } from "@tabler/icons-react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useMediaQuery } from "../hooks/useMediaQuery";

import Home from "./../Pages/Home/Home";
import Logo from "../assets/logo_1.png";
import LogoH from "../assets/logo_h.png";
import home from "../assets/home.png";
import calendar from "../assets/calendar.png";
import clients from "../assets/clients.png";
import bussiness from "../assets/bussiness.png";
import chat from "../assets/chat.png";
import analytics from "../assets/analytics.png";
import invoice from "../assets/invoice.png";
import payments from "../assets/payments.png";
import marketplace from "../assets/marketplace.png";
import Analytics from "../Pages/Analytics/Analytics";
import Calendar from "../Calendar/Calendar";
import Clients from "../Pages/Clients/Clients";
import ExcludedClients from "../Pages/Clients/ExcludedClients";
import YourBusiness from "../Pages/YourBussiness/YourBussiness";
import Chat from "../Pages/Chat/Chat";
import Payment from "../Pages/Payment/Payment";
import Settings from "../Pages/Settings/Settings";
import Marketplace from "../Pages/Marketplace/Marketplace";
import Invoices from "../Pages/Invoices/Invoices";
import Taxes from "../Pages/Taxes/Taxes";
import Affiliate from "../Pages/Affiliate/Affiliate";
import { REST_API_URL } from "../api/config";
import { getHalperChatUnreadCount } from "../api/chat/chatServices";
import { buildHeaders } from "../api/buildHeaders";
import { saveToken } from "../utils/localStorage";

const API_URL = REST_API_URL;

const looksLikeJwt = (t) =>
  typeof t === "string" &&
  t &&
  t !== "undefined" &&
  t !== "null" &&
  t.split(".").length === 3;

const clearAuthStorage = () => {
  // Keys used by smartAxios interceptor (getToken/saveToken)
  localStorage.removeItem("access");
  localStorage.removeItem("refresh");

  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("user_status");
  localStorage.removeItem("halper_remember_me");
  localStorage.removeItem("halper_saved_email");
  localStorage.removeItem("last_login_time");
  localStorage.removeItem("login_timezone");
  localStorage.removeItem("time_zone");
  localStorage.removeItem("analyticsCurrency");
  localStorage.removeItem("analyticsCurrencyTimestamp");
  localStorage.removeItem("redirectAfterAuth");

  sessionStorage.removeItem("accessToken");
  sessionStorage.removeItem("refreshToken");
  sessionStorage.removeItem("user_status");
  sessionStorage.removeItem("time_zone");
  sessionStorage.removeItem("last_login_time");
  sessionStorage.removeItem("login_timezone");
  sessionStorage.removeItem("onboarding_force_route");
  sessionStorage.removeItem("onboarding_force_status");

  // Clean up tab-specific "should clear" flags (see main.jsx)
  Object.keys(localStorage)
    .filter((k) => k.startsWith("halper_should_clear_"))
    .forEach((k) => localStorage.removeItem(k));
};

export function SidebarDemo() {
  const { t, i18n } = useTranslation();
  const mainLinks = useMemo(() => [
    { key: "Home", label: t("sidebar.home"), icon: home, path: "/profile" },
    { key: "Calendar", label: t("sidebar.calendar"), icon: calendar, path: "/calendar" },
    { key: "Clients", label: t("sidebar.clients"), icon: clients, path: "/clients" },
    {
      key: "Excluded contacts",
      label: t("sidebar.excludedContacts"),
      icon: clients,
      path: "/excluded-contacts",
    },
    { key: "Your business", label: t("sidebar.yourBusiness"), icon: bussiness, path: "/your-business" },
    { key: "Chat", label: t("sidebar.chat"), icon: chat, path: "/chat" },
    { key: "Analytics", label: t("sidebar.analytics"), icon: analytics, path: "/analytics" },
    { key: "Affiliate", label: t("sidebar.affiliate", "Affiliate"), icon: IconUsersGroup, iconComponent: true, path: "/affiliate" },
    { key: "Invoices", label: t("sidebar.invoices"), icon: invoice, path: "/invoices" },
    { key: "Payments", label: t("sidebar.payments"), icon: payments, path: "/payments" },
    { key: "Marketplace", label: t("sidebar.marketplace"), icon: marketplace, path: "/marketplace" },
    { key: "Accounting", label: t("sidebar.accounting"), icon: IconReceipt2, iconComponent: true, badge: "New", path: "/accounting" },
  ], [t]);

  const bottomLinks = useMemo(() => [
    { key: "Settings", label: t("sidebar.settings"), icon: IconSettings, path: "/settings" },
  ], [t]);

  const [open, setOpen] = useState(true);
  const isMobile = useMediaQuery("(max-width: 767px)");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [activeLink, setActiveLink] = useState("Home");
  const [taxesLoaded, setTaxesLoaded] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isMenuHovered, setIsMenuHovered] = useState(false);
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);
  const [halperChatUnreadCount, setHalperChatUnreadCount] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const allLinks = [...mainLinks, ...bottomLinks];
    
    // 1. Check if we have an explicit ?section= search param for backward compatibility
    const section = searchParams.get("section");
    if (section) {
      const linkBySection = allLinks.find(link => link.key === section);
      if (linkBySection) {
        setActiveLink(linkBySection.key);
        return;
      }
    }

    // 2. Otherwise match current pathname to link path
    const matchingLink = allLinks.find(link => link.path === location.pathname);
    if (matchingLink) {
      setActiveLink(matchingLink.key);
    }
  }, [searchParams, location.pathname, mainLinks, bottomLinks]);

  useEffect(() => {
    if (activeLink === "Accounting") setTaxesLoaded(true);
  }, [activeLink]);

  // Close the mobile drawer whenever the active section or route changes.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [activeLink, location.pathname]);

  // Leaving mobile width should always dismiss the drawer so it can't linger.
  useEffect(() => {
    if (!isMobile) setMobileNavOpen(false);
  }, [isMobile]);

  // Prevent the page behind the drawer from scrolling while it is open.
  useEffect(() => {
    if (mobileNavOpen) {
      const previous = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = previous;
      };
    }
  }, [mobileNavOpen]);

  useEffect(() => {
    getHalperChatUnreadCount()
      .then((data) => setHalperChatUnreadCount(data.unread_count))
      .catch(console.error);
  }, [activeLink]);

  const fetchUserInfo = useCallback(async () => {
    const token =
      localStorage.getItem("accessToken") ||
      sessionStorage.getItem("accessToken");

    if (!looksLikeJwt(token)) {
      setUserInfo(null);
      setLoadingUser(false);
      return;
    }

    try {
      setLoadingUser(true);

      const headers = buildHeaders();
      const res = await fetch(`${API_URL}/auth/userinfo/`, { headers });

      if (res.status === 401) {
        clearAuthStorage();
        navigate("/sign-in", { replace: true });
        return;
      }

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json().catch(() => null);
      if (data?.user?.primary_language?.name) {
        i18n.changeLanguage(data.user.primary_language.name);
      }
      if (data?.time_zone) {
        saveToken("time_zone", data.time_zone);
      }
      setUserInfo(data);
    } catch (err) {
      console.error("❌ Failed to fetch user info:", err);
      setUserInfo(null);
    } finally {
      setLoadingUser(false);
    }
  }, [navigate]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (cancelled) return;
      await fetchUserInfo();
    };

    run();

    const handler = () => fetchUserInfo();
    window.addEventListener("userUpdated", handler);

    return () => {
      cancelled = true;
      window.removeEventListener("userUpdated", handler);
    };
  }, [fetchUserInfo]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const user = userInfo?.user || {};
 
  const userName =
    [user.first_name, user.last_name].filter(Boolean).join(" ") ||
    user.email ||
    t("sidebar.defaultUser");

  const userAvatar =
    userInfo?.user?.image_url ||
    userInfo?.image ||
    userInfo?.avatar ||
    "https://i.sstatic.net/l60Hf.png";

  const handleSignOut = () => {
    clearAuthStorage();
    navigate("/sign-in", { replace: true });
  };

  const handleLanguageChange = async (code) => {
    try {
      i18n.changeLanguage(code);
      const token =
        localStorage.getItem("accessToken") ||
        sessionStorage.getItem("accessToken");

      if (looksLikeJwt(token)) {
        const res = await fetch(`${API_URL}/auth/change-main-language/`, {
          method: "POST",
          headers: buildHeaders(),
          body: JSON.stringify({ main_language: code }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          toast.error(
            data?.detail ||
              data?.message ||
              "Error updating language on server",
          );
          return;
        }
      }
      setTimeout(() => {
        window.location.reload();
      }, 150);
    } catch (err) {
      console.error("Failed to update language on server:", err);
      toast.error("Network error updating language");
    }
  };

  return (
    <div className="flex h-[100dvh] w-full overflow-hidden bg-gray-50">
      <motion.div
        animate={{ width: open ? "auto" : 64 }}
        className="hidden md:block h-full border-r border-neutral-200 bg-white"
        transition={{ duration: 0.2, ease: "easeInOut" }}
      >
        <div className="flex h-full flex-col justify-between">
          <div>
            <div
              className={`flex items-center p-4 w-full ${open ? "justify-between" : "justify-center"}`}
            >
              <AnimatePresence>
                {open && (
                  <motion.img
                    src={Logo}
                    alt="Logo"
                    key="halper"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -10 }}
                    transition={{ duration: 0.2 }}
                    className="max-w-[150px] h-auto object-contain w-full"
                  />
                )}
              </AnimatePresence>

              <button
                type="button"
                onClick={() => setOpen(!open)}
                onMouseEnter={() => setIsMenuHovered(true)}
                onMouseLeave={() => setIsMenuHovered(false)}
                className={`text-neutral-700 hover:bg-neutral-100 p-1 rounded transition-all flex items-center justify-center ${
                  !open ? "w-8 h-8 relative overflow-hidden" : ""
                }`}
              >
                {!open ? (
                  <AnimatePresence mode="wait">
                    {isMenuHovered ? (
                      <motion.span
                        key="menu-icon"
                        initial={{ opacity: 0, scale: 0.7, rotate: -45 }}
                        animate={{ opacity: 1, scale: 1, rotate: 0 }}
                        exit={{ opacity: 0, scale: 0.7, rotate: 45 }}
                        transition={{ duration: 0.12 }}
                        className="flex items-center justify-center w-6 h-6 text-neutral-700 absolute"
                      >
                        <CiMenuFries className="h-5 w-5" />
                      </motion.span>
                    ) : (
                      <motion.span
                        key="logo-h"
                        initial={{ opacity: 0, scale: 0.7 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.7 }}
                        transition={{ duration: 0.12 }}
                        className="flex items-center justify-center w-6 h-6 absolute"
                      >
                        <img
                          src={LogoH}
                          alt="Logo H"
                          className="w-full h-full object-contain rounded"
                        />
                      </motion.span>
                    )}
                  </AnimatePresence>
                ) : (
                  <CiMenuFries className="h-5 w-5" />
                )}
              </button>
            </div>

            <nav className="flex flex-col gap-1 px-2 mt-2">
              {mainLinks
                .filter((link) => {
                  if (
                    (link.key === "Marketplace" || link.key === "Accounting") &&
                    userInfo?.user?.status === "worker"
                  ) {
                    return false;
                  }
                  if (link.key === "Affiliate" && !userInfo?.affiliate) {
                    return false;
                  }
                  return true;
                })
                .map((link, idx) => {
                  const isActive = activeLink === link.key;
                  return (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => {
                        if (link.key === "Marketplace") {
                          window.dispatchEvent(new CustomEvent("openMarketplaceCompareModal"));
                        }
                        if (link.path) {
                          navigate(link.path);
                        } else {
                          setActiveLink(link.key);
                          if (location.pathname !== "/profile") {
                            navigate("/profile");
                          } else {
                            setSearchParams({});
                          }
                        }
                      }}
                      className={`relative flex items-center justify-between gap-3 px-3 p-2.5 text-sm transition-all  ${
                        isActive
                          ? "bg-[#020637] text-white font-medium rounded-xl"
                          : "text-neutral-700 hover:bg-neutral-100 rounded-md"
                      }`}
                    >
                      {isActive && (
                        <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-10 bg-[#020637] rounded-r -ml-2" />
                      )}
                      <div className="flex items-center gap-3 min-w-0">
                        {link.iconComponent ? (
                          <link.icon
                            className={`h-5 w-5 shrink-0 ${
                              isActive ? "text-white" : "text-neutral-500"
                            }`}
                          />
                        ) : (
                          <img
                            src={link.icon}
                            alt={link.label}
                            className={`h-5 w-5 shrink-0 ${
                              isActive ? "brightness-200" : "opacity-70"
                            }`}
                          />
                        )}
                        {open && <span>{link.label}</span>}
                        {open && link.badge && (
                          <span className="ml-1 inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-full bg-white text-[#020637]">
                            {link.badge}
                          </span>
                        )}
                      </div>
                      {/* Show unread badge only for Chat link, visually appealing */}

                      {link.key === "Chat" &&
                        halperChatUnreadCount > 0 &&
                        (open ? (
                          <span
                            className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-semibold rounded-full bg-red-500 text-white min-w-[20px] min-h-[20px]"
                            style={{ lineHeight: 1 }}
                            aria-label={t("sidebar.unreadMessages", {
                              count: halperChatUnreadCount,
                            })}
                          >
                            {halperChatUnreadCount > 99
                              ? "99+"
                              : halperChatUnreadCount}
                          </span>
                        ) : (
                          <span
                            className="absolute -top-[0px] -right-[0px] inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-semibold  bg-red-500 rounded-full  text-white "
                            aria-label={t("sidebar.unreadMessages", {
                              count: halperChatUnreadCount,
                            })}
                          >
                            {halperChatUnreadCount > 99
                              ? "99+"
                              : halperChatUnreadCount}
                          </span>
                        ))}
                    </button>
                  );
                })}
            </nav>
          </div>

          <div className="mb-4 px-2 flex flex-col gap-1">
            {/* Language Switcher */}
            {!open ? (
              <div
                className="w-full flex flex-col items-center justify-center py-2"
                onMouseEnter={() => setIsLangDropdownOpen(true)}
                onMouseLeave={() => setIsLangDropdownOpen(false)}
              >
                <AnimatePresence mode="wait">
                  {!isLangDropdownOpen ? (
                    <motion.button
                      key="collapsed-lang-btn"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.9 }}
                      transition={{ duration: 0.1 }}
                      type="button"
                      className="w-8 h-8 flex items-center justify-center border border-neutral-200 rounded-lg text-[10px] leading-none font-bold bg-neutral-50 text-neutral-500 shadow-sm transition-all hover:border-[#020637] hover:text-[#0e1c7a] hover:bg-white"
                      title="Select Language"
                    >
                      {i18n.language?.startsWith("es") ? "ES" : "EN"}
                    </motion.button>
                  ) : (
                    <motion.div
                      key="collapsed-lang-list"
                      initial={{ opacity: 0, height: 32 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 32 }}
                      transition={{ duration: 0.12 }}
                      className="flex flex-col gap-1 bg-neutral-50 border border-neutral-200/80 p-1 rounded-xl shadow-inner overflow-hidden"
                    >
                      <button
                        type="button"
                        onClick={() => handleLanguageChange("en")}
                        className={`w-8 h-8 flex items-center justify-center rounded-lg text-[10px] leading-none font-bold transition-all ${
                          !i18n.language?.startsWith("es")
                            ? "bg-white text-[#020637] shadow-sm border border-neutral-200/40"
                            : "text-neutral-400 hover:text-neutral-700 hover:bg-neutral-200/40"
                        }`}
                      >
                        EN
                      </button>
                      <button
                        type="button"
                        onClick={() => handleLanguageChange("es")}
                        className={`w-8 h-8 flex items-center justify-center rounded-lg text-[10px] leading-none font-bold transition-all ${
                          i18n.language?.startsWith("es")
                            ? "bg-white text-[#020637] shadow-sm border border-neutral-200/40"
                            : "text-neutral-400 hover:text-neutral-700 hover:bg-neutral-200/40"
                        }`}
                      >
                        ES
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <div className="flex items-center justify-between bg-neutral-50 border border-neutral-200/60 p-1 rounded-xl text-xs font-semibold text-neutral-500 mb-2.5 transition-all mx-1 shadow-inner">
                <button
                  type="button"
                  onClick={() => handleLanguageChange("en")}
                  className={`flex-1 py-1.5 text-center rounded-lg transition-all ${
                    !i18n.language?.startsWith("es")
                      ? "bg-white text-[#020637] shadow-sm font-bold border border-neutral-200/50"
                      : "hover:text-neutral-800 hover:bg-neutral-200/40 text-neutral-400"
                  }`}
                >
                  English
                </button>
                <button
                  type="button"
                  onClick={() => handleLanguageChange("es")}
                  className={`flex-1 py-1.5 text-center rounded-lg transition-all ${
                    i18n.language?.startsWith("es")
                      ? "bg-white text-[#020637] shadow-sm font-bold border border-neutral-200/50"
                      : "hover:text-neutral-800 hover:bg-neutral-200/40 text-neutral-400"
                  }`}
                >
                  Español
                </button>
              </div>
            )}

            {bottomLinks.map((link, idx) => {
              const Icon = link.icon;
              const isActive = activeLink === link.key;

              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => {
                    if (link.path) {
                      navigate(link.path);
                    } else {
                      setActiveLink(link.key);
                      setSearchParams({});
                    }
                  }}
                  className={`relative flex items-center gap-3 px-3 py-2.5 text-sm transition-all w-full ${
                    isActive
                      ? "bg-[#020637] text-white font-medium rounded-xl"
                      : "text-neutral-700 hover:bg-neutral-100 rounded-md"
                  }`}
                >
                  {isActive && (
                    <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-10 bg-[#020637] rounded-r -ml-2" />
                  )}
                  <Icon
                    className={`h-5 w-5 shrink-0 ${
                      isActive ? "text-white" : "text-neutral-500"
                    }`}
                  />
                  {open && <span>{link.label}</span>}
                </button>
              );
            })}

            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="w-full px-3 py-2.5 mt-2 rounded-md text-sm text-neutral-700 hover:bg-neutral-100 transition-all flex items-center justify-between"
              >
                {loadingUser ? (
                  <span className="text-neutral-400">
                    {t("sidebar.loading")}
                  </span>
                ) : (
                  <>
                    <div className="flex items-center gap-3 min-w-0">
                      <img
                        src={userAvatar}
                        className="w-8 h-8 shrink-0 rounded-full object-cover"
                        alt="avatar"
                      />

                      {open && (
                        <span className="font-medium truncate max-w-[100px]">
                          {userName}
                        </span>
                      )}
                    </div>

                    {open && (
                      <HiMiniChevronRight
                        className={`transition-transform duration-200 ${
                          dropdownOpen ? "rotate-90" : ""
                        }`}
                      />
                    )}
                  </>
                )}
              </button>

              <AnimatePresence>
                {dropdownOpen && open && (
                  <motion.div
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -5 }}
                    transition={{ duration: 0.15 }}
                    className="absolute bottom-14 left-3 w-[150px] bg-white border border-neutral-200 shadow-lg rounded-lg overflow-hidden z-20"
                  >
                    <button
                      type="button"
                      onClick={handleSignOut}
                      className="flex items-center gap-2 px-4 py-2 text-sm w-full text-left hover:bg-neutral-100 text-red-500"
                    >
                      <IconLogout2 className="h-4 w-4" /> {t("sidebar.signOut")}
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Mobile off-canvas drawer + backdrop */}
      <AnimatePresence>
        {mobileNavOpen && (
          <>
            <motion.div
              key="mobile-nav-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setMobileNavOpen(false)}
              className="md:hidden fixed inset-0 bg-black/40 z-[90]"
              aria-hidden="true"
            />
            <motion.aside
              key="mobile-nav-drawer"
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ duration: 0.25, ease: "easeInOut" }}
              className="md:hidden fixed inset-y-0 left-0 w-[82%] max-w-[300px] bg-white z-[100] shadow-xl flex flex-col overflow-y-auto"
              role="dialog"
              aria-label={t("sidebar.menu", "Menu")}
            >
              {/* Drawer header */}
              <div className="flex items-center justify-between p-4 border-b border-neutral-200">
                <img
                  src={Logo}
                  alt="Logo"
                  className="max-w-[140px] h-auto w-full object-contain"
                />
                <button
                  type="button"
                  onClick={() => setMobileNavOpen(false)}
                  aria-label={t("sidebar.closeMenu", "Close menu")}
                  className="shrink-0 text-neutral-700 hover:bg-neutral-100 p-1.5 rounded-lg transition-all"
                >
                  <IoClose className="h-6 w-6" />
                </button>
              </div>

              {/* Drawer nav */}
              <nav className="flex flex-col gap-1 px-2 mt-3 flex-1">
                {mainLinks
                  .filter((link) => {
                    if (
                      (link.key === "Marketplace" ||
                        link.key === "Accounting") &&
                      userInfo?.user?.status === "worker"
                    ) {
                      return false;
                    }
                    if (link.key === "Affiliate" && !userInfo?.affiliate) {
                      return false;
                    }
                    return true;
                  })
                  .map((link) => {
                    const isActive = activeLink === link.key;
                    return (
                      <button
                        key={`m-${link.key}`}
                        type="button"
                        onClick={() => {
                          setMobileNavOpen(false);
                          if (link.key === "Marketplace") {
                            window.dispatchEvent(
                              new CustomEvent("openMarketplaceCompareModal"),
                            );
                          }
                          if (link.path) {
                            navigate(link.path);
                          } else {
                            setActiveLink(link.key);
                            if (location.pathname !== "/profile") {
                              navigate("/profile");
                            } else {
                              setSearchParams({});
                            }
                          }
                        }}
                        className={`relative flex items-center justify-between gap-3 px-3 py-3 text-sm transition-all ${
                          isActive
                            ? "bg-[#020637] text-white font-medium rounded-xl"
                            : "text-neutral-700 hover:bg-neutral-100 rounded-md"
                        }`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          {link.iconComponent ? (
                            <link.icon
                              className={`h-5 w-5 shrink-0 ${
                                isActive ? "text-white" : "text-neutral-500"
                              }`}
                            />
                          ) : (
                            <img
                              src={link.icon}
                              alt=""
                              className={`h-5 w-5 shrink-0 ${
                                isActive ? "brightness-200" : "opacity-70"
                              }`}
                            />
                          )}
                          <span className="truncate">{link.label}</span>
                          {link.badge && (
                            <span className="ml-1 inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-full bg-white text-[#020637]">
                              {link.badge}
                            </span>
                          )}
                        </div>
                        {link.key === "Chat" && halperChatUnreadCount > 0 && (
                          <span
                            className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-semibold rounded-full bg-red-500 text-white min-w-[20px] min-h-[20px]"
                            style={{ lineHeight: 1 }}
                            aria-label={t("sidebar.unreadMessages", {
                              count: halperChatUnreadCount,
                            })}
                          >
                            {halperChatUnreadCount > 99
                              ? "99+"
                              : halperChatUnreadCount}
                          </span>
                        )}
                      </button>
                    );
                  })}

                {bottomLinks.map((link) => {
                  const Icon = link.icon;
                  const isActive = activeLink === link.key;
                  return (
                    <button
                      key={`m-${link.key}`}
                      type="button"
                      onClick={() => {
                        setMobileNavOpen(false);
                        if (link.path) {
                          navigate(link.path);
                        } else {
                          setActiveLink(link.key);
                          setSearchParams({});
                        }
                      }}
                      className={`relative flex items-center gap-3 px-3 py-3 text-sm transition-all w-full ${
                        isActive
                          ? "bg-[#020637] text-white font-medium rounded-xl"
                          : "text-neutral-700 hover:bg-neutral-100 rounded-md"
                      }`}
                    >
                      <Icon
                        className={`h-5 w-5 shrink-0 ${
                          isActive ? "text-white" : "text-neutral-500"
                        }`}
                      />
                      <span className="truncate">{link.label}</span>
                    </button>
                  );
                })}
              </nav>

              {/* Drawer footer: language + user + sign out */}
              <div className="mt-auto px-2 pb-4 pt-2 border-t border-neutral-200 flex flex-col gap-3">
                <div className="flex items-center justify-between bg-neutral-50 border border-neutral-200/60 p-1 rounded-xl text-xs font-semibold text-neutral-500 mx-1 shadow-inner">
                  <button
                    type="button"
                    onClick={() => handleLanguageChange("en")}
                    className={`flex-1 py-2 text-center rounded-lg transition-all ${
                      !i18n.language?.startsWith("es")
                        ? "bg-white text-[#020637] shadow-sm font-bold border border-neutral-200/50"
                        : "hover:text-neutral-800 hover:bg-neutral-200/40 text-neutral-400"
                    }`}
                  >
                    English
                  </button>
                  <button
                    type="button"
                    onClick={() => handleLanguageChange("es")}
                    className={`flex-1 py-2 text-center rounded-lg transition-all ${
                      i18n.language?.startsWith("es")
                        ? "bg-white text-[#020637] shadow-sm font-bold border border-neutral-200/50"
                        : "hover:text-neutral-800 hover:bg-neutral-200/40 text-neutral-400"
                    }`}
                  >
                    Español
                  </button>
                </div>

                <div className="flex items-center gap-3 px-3 min-w-0">
                  <img
                    src={userAvatar}
                    className="w-9 h-9 shrink-0 rounded-full object-cover"
                    alt="avatar"
                  />
                  <span className="font-medium truncate">
                    {loadingUser ? t("sidebar.loading") : userName}
                  </span>
                </div>

                <button
                  type="button"
                  onClick={handleSignOut}
                  className="flex items-center gap-2 px-3 py-3 text-sm w-full text-left hover:bg-neutral-100 rounded-md text-red-500"
                >
                  <IconLogout2 className="h-5 w-5" /> {t("sidebar.signOut")}
                </button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Content column */}
      <div className="flex flex-1 flex-col min-w-0 h-full overflow-hidden">
        {/* Mobile top bar */}
        <div className="md:hidden flex items-center justify-between gap-3 px-4 py-3 border-b border-neutral-200 bg-white shrink-0">
          <button
            type="button"
            onClick={() => setMobileNavOpen(true)}
            aria-label={t("sidebar.openMenu", "Open menu")}
            className="text-neutral-700 hover:bg-neutral-100 p-1.5 rounded-lg transition-all"
          >
            <CiMenuFries className="h-6 w-6" />
          </button>
          <img
            src={Logo}
            alt="Logo"
            className="h-7 w-auto object-contain"
          />
          <div className="w-9" aria-hidden="true" />
        </div>

        <main className="flex flex-1 flex-col overflow-auto p-1">
        {activeLink === "Home" && <Home />}
        {activeLink === "Calendar" && <Calendar />}
        {activeLink === "Clients" && <Clients />}
        {activeLink === "Excluded contacts" && <ExcludedClients />}
        {activeLink === "Your business" && <YourBusiness />}
        {activeLink === "Chat" && <Chat />}
        {activeLink === "Analytics" && <Analytics />}
        {activeLink === "Payments" && <Payment />}
        {activeLink === "Settings" && (
          <Settings initialTab={searchParams.get("tab") ?? "user-info"} />
        )}
        {/* {activeLink === "Support" && <SupportChat />} */}
        {activeLink === "Affiliate" && <Affiliate />}
        {activeLink === "Marketplace" && <Marketplace />}
        {activeLink === "Invoices" && <Invoices />}
        {taxesLoaded && (
          <div
            className="flex flex-1 flex-col overflow-auto"
            style={{ display: activeLink === "Accounting" ? undefined : "none" }}
          >
            <Taxes />
          </div>
        )}
      </main>
      </div>
    </div>
  );
}
